import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import {
  calculateFraction,
  MAX_INTEGER_INPUT_LENGTH,
  parseInteger,
  type CalculationError,
  type CalculationState,
  type FieldKey,
  type FractionInputs,
  type FractionMode,
} from '@/pages/fraction-calculator/fraction-calculator'
import { fractionCalculatorRoute } from '@/shared/utility-registry'

export { calculateFraction, mixedNumberText, reduceFraction } from '@/pages/fraction-calculator/fraction-calculator'
export type { FractionMode } from '@/pages/fraction-calculator/fraction-calculator'

const UTILITY = fractionCalculatorRoute.analyticsKey

interface TimedCalculation {
  state: CalculationState
  durationMs: number
}

interface ModeOption {
  key: FractionMode
  label: string
  shortLabel: string
  description: string
}

const modes: ModeOption[] = [
  { key: 'add', label: 'Сложение дробей', shortLabel: 'Сложить', description: 'a/b + c/d' },
  { key: 'subtract', label: 'Вычитание дробей', shortLabel: 'Вычесть', description: 'a/b − c/d' },
  { key: 'multiply', label: 'Умножение дробей', shortLabel: 'Умножить', description: 'a/b × c/d' },
  { key: 'divide', label: 'Деление дробей', shortLabel: 'Разделить', description: 'a/b ÷ c/d' },
  { key: 'simplify', label: 'Сокращение дроби', shortLabel: 'Сократить', description: 'Привести к простому виду' },
  { key: 'mixed-to-improper', label: 'Смешанная → неправильная', shortLabel: 'В неправильную', description: '2 1/3 → 7/3' },
  { key: 'improper-to-mixed', label: 'Неправильная → смешанная', shortLabel: 'В смешанную', description: '7/3 → 2 1/3' },
]

const initialInputs: FractionInputs = {
  firstNumerator: '',
  firstDenominator: '',
  secondNumerator: '',
  secondDenominator: '',
  whole: '',
}

export function FractionCalculatorPage() {
  const [mode, setMode] = useState<FractionMode>('add')
  const [inputs, setInputs] = useState<FractionInputs>(initialInputs)
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle')
  const lastAnalyticsSnapshot = useRef<string | null>(null)

  const activeMode = useMemo(() => modes.find((item) => item.key === mode) ?? modes[0]!, [mode])
  const calculationRun = useMemo<TimedCalculation>(() => {
    const startMs = performance.now()
    const state = calculateFraction(mode, inputs)

    return { state, durationMs: performance.now() - startMs }
  }, [inputs, mode])
  const calculation = calculationRun.state

  useEffect(() => {
    if (!calculation.snapshot || calculation.snapshot === lastAnalyticsSnapshot.current) return

    const timeout = window.setTimeout(() => {
      if (!calculation.snapshot || calculation.snapshot === lastAnalyticsSnapshot.current) return
      lastAnalyticsSnapshot.current = calculation.snapshot

      toolInvoke(UTILITY, { mode })
      if (calculation.status === 'ready') {
        toolSuccess(UTILITY, calculationRun.durationMs, { mode })
      } else if (calculation.error) {
        toolError(UTILITY, calculation.error, { mode })
      }
    }, 500)

    return () => window.clearTimeout(timeout)
  }, [calculation.error, calculation.snapshot, calculation.status, calculationRun.durationMs, mode])

  const updateInput = useCallback((field: FieldKey) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setInputs((current) => ({ ...current, [field]: value }))
    setCopyStatus('idle')
  }, [])

  const reset = useCallback(() => {
    setInputs(initialInputs)
    setCopyStatus('idle')
    lastAnalyticsSnapshot.current = null
  }, [])

  const copyResult = useCallback(async () => {
    if (!calculation.result || !navigator.clipboard) {
      setCopyStatus('error')
      return
    }

    try {
      await navigator.clipboard.writeText(calculation.result.display)
      setCopyStatus('copied')
    } catch {
      setCopyStatus('error')
    }
  }, [calculation.result])

  const isBinary = mode === 'add' || mode === 'subtract' || mode === 'multiply' || mode === 'divide'
  const isMixedInput = mode === 'mixed-to-improper'

  return (
    <div className="mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-teal-600 via-amber-400 to-red-500" />
        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.86fr)]">
          <div className="min-w-0 space-y-3 border-b p-4 sm:space-y-5 sm:p-6 lg:border-b-0 lg:border-r">
            <label htmlFor="fraction-mode" className="block">
              <span className="mb-1 block text-sm font-medium">Действие</span>
              <select
                id="fraction-mode"
                value={mode}
                onChange={(event) => {
                  setMode(event.target.value as FractionMode)
                  setCopyStatus('idle')
                }}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring/20"
              >
                {modes.map((item) => <option key={item.key} value={item.key}>{item.shortLabel}</option>)}
              </select>
              <span className="mt-2 block text-xs leading-5 text-muted-foreground">
                {activeMode.label}: {activeMode.description}
              </span>
            </label>

            <div className="sm:rounded-md sm:border sm:bg-background/70 sm:p-4 sm:shadow-inner">
              {isMixedInput && (
                <div className="mb-4">
                  <IntegerField
                    id="whole"
                    label="Целая часть (необязательно)"
                    value={inputs.whole}
                    placeholder="2"
                    onChange={updateInput('whole')}
                    invalid={calculation.invalidFields.includes('whole')}
                  />
                </div>
              )}

              <div className={cn('grid min-w-0 gap-4', isBinary && 'sm:grid-cols-2')}>
                <FractionFields
                  prefix="first"
                  label={isMixedInput ? 'Дробная часть' : 'Первая дробь'}
                  numerator={inputs.firstNumerator}
                  denominator={inputs.firstDenominator}
                  numeratorPlaceholder="1"
                  denominatorPlaceholder="2"
                  onNumeratorChange={updateInput('firstNumerator')}
                  onDenominatorChange={updateInput('firstDenominator')}
                  invalidFields={calculation.invalidFields}
                />
                {isBinary && (
                  <FractionFields
                    prefix="second"
                    label="Вторая дробь"
                    numerator={inputs.secondNumerator}
                    denominator={inputs.secondDenominator}
                    numeratorPlaceholder="1"
                    denominatorPlaceholder="3"
                    onNumeratorChange={updateInput('secondNumerator')}
                    onDenominatorChange={updateInput('secondDenominator')}
                    invalidFields={calculation.invalidFields}
                  />
                )}
              </div>

              {calculation.error && (
                <div id="fraction-input-error" role="alert" className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {errorText(calculation.error)}
                </div>
              )}
            </div>

          </div>

          <ResultPanel calculation={calculation}>
            <ResultActions hasResult={Boolean(calculation.result)} copyStatus={copyStatus} onCopy={copyResult} onReset={reset} />
          </ResultPanel>
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <InfoCard title="Точная арифметика" text="Целые компоненты дробей считаются без округления." />
        <InfoCard title="Автосокращение" text="Каждый результат приводится к простому виду." />
        <InfoCard title="Без сервера" text="Введённые числа остаются в вашем браузере." />
      </section>
    </div>
  )
}

function FractionFields({
  prefix,
  label,
  numerator,
  denominator,
  numeratorPlaceholder,
  denominatorPlaceholder,
  onNumeratorChange,
  onDenominatorChange,
  invalidFields,
}: {
  prefix: string
  label: string
  numerator: string
  denominator: string
  numeratorPlaceholder: string
  denominatorPlaceholder: string
  onNumeratorChange: (event: ChangeEvent<HTMLInputElement>) => void
  onDenominatorChange: (event: ChangeEvent<HTMLInputElement>) => void
  invalidFields: FieldKey[]
}) {
  return (
    <fieldset className="min-w-0 rounded-md border bg-card p-3">
      <legend className="px-1 text-sm font-medium">{label}</legend>
      <div className="mx-auto max-w-56">
        <IntegerField
          id={`${prefix}-numerator`}
          label="Числитель"
          value={numerator}
          placeholder={numeratorPlaceholder}
          onChange={onNumeratorChange}
          invalid={invalidFields.includes(`${prefix}Numerator` as FieldKey)}
          visuallyCompact
        />
        <div aria-hidden="true" className="my-2 h-px bg-foreground/55" />
        <IntegerField
          id={`${prefix}-denominator`}
          label="Знаменатель"
          value={denominator}
          placeholder={denominatorPlaceholder}
          onChange={onDenominatorChange}
          invalid={invalidFields.includes(`${prefix}Denominator` as FieldKey)}
          visuallyCompact
        />
      </div>
    </fieldset>
  )
}

function IntegerField({
  id,
  label,
  value,
  placeholder,
  onChange,
  invalid = false,
  visuallyCompact = false,
}: {
  id: string
  label: string
  value: string
  placeholder: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  invalid?: boolean
  visuallyCompact?: boolean
}) {
  const isInvalid = invalid || (value.trim() !== '' && parseInteger(value) === null)

  return (
    <label htmlFor={id} className="block min-w-0">
      <span className={cn('mb-1 block text-xs text-muted-foreground', visuallyCompact && 'text-center')}>{label}</span>
      <input
        id={id}
        inputMode="numeric"
        maxLength={MAX_INTEGER_INPUT_LENGTH}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        aria-invalid={isInvalid}
        aria-describedby={isInvalid ? 'fraction-input-error' : undefined}
        className={cn(
          'h-11 w-full min-w-0 rounded-md border bg-background px-3 font-mono text-base tabular-nums outline-none transition-colors',
          'focus:border-primary focus:ring-2 focus:ring-ring/20',
          visuallyCompact && 'text-center',
          isInvalid && 'border-destructive focus:border-destructive focus:ring-destructive/20',
        )}
      />
    </label>
  )
}

function ResultActions({ hasResult, copyStatus, onCopy, onReset }: {
  hasResult: boolean
  copyStatus: 'idle' | 'copied' | 'error'
  onCopy: () => void
  onReset: () => void
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {hasResult && <Button variant="outline" onClick={onCopy}>Скопировать результат</Button>}
      <Button variant="ghost" onClick={onReset}>Сбросить</Button>
      <span role="status" className="text-sm text-primary">{copyStatus === 'copied' ? 'Результат скопирован.' : ''}</span>
      <span role="alert" className="text-sm text-destructive">{copyStatus === 'error' ? 'Не удалось скопировать результат.' : ''}</span>
    </div>
  )
}

function ResultPanel({ calculation, children }: { calculation: CalculationState; children: ReactNode }) {
  return (
    <div aria-live="polite" aria-atomic="true" className="flex min-w-0 flex-col bg-background/55 p-4 sm:p-6 lg:min-h-[390px]">
      <div className="text-sm font-medium text-muted-foreground">Результат</div>
      {calculation.result ? (
        <div className="mt-2 min-w-0 sm:mt-4">
          <div className="rounded-md border border-primary/35 bg-card p-5 shadow-sm">
            <div className="break-words font-mono text-3xl font-semibold tabular-nums sm:text-4xl">
              {calculation.result.display}
            </div>
          </div>
          <div className="mt-5 rounded-md border bg-card p-4">
            <div className="text-xs font-medium uppercase text-muted-foreground">Формула и шаги</div>
            <div className="mt-2 break-words font-mono text-sm">{calculation.result.formula}</div>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-muted-foreground">
              {calculation.result.steps.map((step) => <li key={step}>{step}</li>)}
            </ul>
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-md border bg-card p-4 shadow-sm">
          <div className="font-semibold">{calculation.status === 'error' ? 'Проверьте значения' : 'Введите дробь'}</div>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {calculation.status === 'error'
              ? 'После исправления полей результат обновится автоматически.'
              : 'Заполните обязательные поля целыми числами — результат появится без отдельной команды.'}
          </p>
        </div>
      )}
      {children}
    </div>
  )
}

function errorText(error: CalculationError): string {
  if (error === 'integer_too_long') return `Введите целое число длиной не более ${MAX_INTEGER_INPUT_LENGTH} символов.`
  if (error === 'zero_denominator') return 'Знаменатель не может быть равен нулю.'
  if (error === 'divide_by_zero_fraction') return 'Нельзя делить на дробь с нулевым числителем.'
  if (error === 'invalid_mixed_numerator') return 'В смешанном числе числитель должен быть неотрицательным и меньше знаменателя.'
  if (error === 'mixed_denominator_not_positive') return 'В смешанном числе знаменатель должен быть положительным.'

  return 'Используйте только целые числа без пробелов и десятичных знаков.'
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return (
    <Card className="rounded-lg">
      <CardContent className="pt-6">
        <div className="text-sm font-semibold">{title}</div>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
      </CardContent>
    </Card>
  )
}
